/**
 * QuotaChip — deck HUD quota meter (design system, FOUNDATION — v2; S-10 HUD).
 * Renders server-reported used/cap only; the client NEVER computes whether an
 * action is allowed — the server rejects excess idempotently (EC-22).
 * v2: the count pops (spring) and the meter fill glides on every change.
 */
import { AnimatePresence, motion } from "framer-motion";
import { springs } from "./motion.js";

export interface QuotaChipProps {
  label: string;
  used: number;
  cap: number;
  /** Remaining fraction at/below which the chip warns (default 0.2). */
  warnAt?: number;
}

export function QuotaChip({ label, used, cap, warnAt = 0.2 }: QuotaChipProps) {
  const remaining = Math.max(0, cap - used);
  const frac = cap > 0 ? remaining / cap : 0;
  const state = remaining === 0 ? "quota--out" : frac <= warnAt ? "quota--low" : "";
  return (
    <div
      className={`quota${state ? ` ${state}` : ""}`}
      aria-label={`${label}: ${remaining} of ${cap} left`}
    >
      <span className="quota__label">{label}</span>
      <span className="quota__count" style={{ display: "inline-flex", gap: 2 }}>
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={remaining}
            initial={{ scale: 1.5, opacity: 0, y: -4 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.6, opacity: 0, y: 4 }}
            transition={springs.bouncy}
            style={{ display: "inline-block" }}
          >
            {remaining}
          </motion.span>
        </AnimatePresence>
        <span>/{cap}</span>
      </span>
      <div className="quota__meter" aria-hidden="true">
        <motion.div
          className="quota__fill"
          initial={false}
          animate={{ width: `${Math.round(frac * 100)}%` }}
          transition={springs.snappy}
        />
      </div>
    </div>
  );
}
